/**
 * Typed error for message decryption failures.
 *
 * Why:
 * - Previously every decrypt call site swallowed any error and returned a
 *   stringly-typed sentinel ("Could not decrypt message") that was then
 *   persisted to IndexedDB forever, with zero observability.
 * - This module gives us a structured error with a discriminating `reason`
 *   so callers can decide whether to retry, downgrade gracefully, or surface
 *   a UI affordance — and so we can log shape (not content) of failures.
 */

import { createLogger } from "@/utils/debug";

const log = createLogger("Crypto");

/**
 * String sentinel that previous versions of the app persisted into
 * `MessageContentEntity.text` whenever decryption failed.
 *
 * Exported from a single place so the legacy-repair startup task can match
 * it without inviting other modules to keep using it. New code must throw
 * `DecryptionError` instead.
 */
export const DECRYPTION_FAILURE_SENTINEL = "Could not decrypt message";

export type DecryptionFailureReason =
  | "MISSING_ECSK"
  | "MISSING_AUTH"
  | "CSK_DECAPSULATION_FAILED"
  | "CIPHERTEXT_DECRYPT_FAILED"
  | "ATTACHMENT_META_DECRYPT_FAILED";

export class DecryptionError extends Error {
  public readonly reason: DecryptionFailureReason;
  public readonly cause?: unknown;

  constructor(reason: DecryptionFailureReason, cause?: unknown) {
    super(`Decryption failed: ${reason}`);
    this.name = "DecryptionError";
    this.reason = reason;
    this.cause = cause;
  }
}

export const isDecryptionError = (value: unknown): value is DecryptionError => {
  return value instanceof DecryptionError;
};

// ── Rate-limited logger ──────────────────────────────────────────────────────
// Coalesce by reason within a 1-second window so a flood of failures doesn't
// spam the console. We log shape only — never ciphertext, plaintext or keys.

const LOG_WINDOW_MS = 1000;
const lastLoggedAt = new Map<DecryptionFailureReason, number>();
const inTabFailureCounts = new Map<DecryptionFailureReason, number>();

export const logDecryptionFailure = (
  reason: DecryptionFailureReason,
  cause?: unknown,
): void => {
  inTabFailureCounts.set(reason, (inTabFailureCounts.get(reason) ?? 0) + 1);

  const now = Date.now();
  const last = lastLoggedAt.get(reason) ?? 0;
  if (now - last < LOG_WINDOW_MS) {
    return;
  }
  lastLoggedAt.set(reason, now);

  // Log shape only. Avoid serializing arbitrary error objects that may
  // contain ciphertext fragments.
  const causeShape =
    cause instanceof Error
      ? { name: cause.name, message: cause.message }
      : typeof cause === "string"
        ? { message: cause }
        : undefined;

  log.warn(`Decryption failed [${reason}]`, causeShape);
};

/**
 * Snapshot of failure counts by reason since this tab was opened (or since
 * the last reset). Used by the diagnostics report; intentionally cumulative
 * so multiple reports in a single session capture trends.
 */
export const getInTabFailureCountsSnapshot = (): Partial<
  Record<DecryptionFailureReason, number>
> => Object.fromEntries(inTabFailureCounts);

/**
 * Clear in-memory logger state (rate-limit window + in-tab failure counts).
 * Called on logout so the next session in the same tab starts clean —
 * otherwise diagnostics would carry counts from the previous user and the
 * rate limiter could suppress legitimate logs in the new session.
 */
export const clearDecryptionLogState = (): void => {
  lastLoggedAt.clear();
  inTabFailureCounts.clear();
};

/**
 * Wraps an async operation, normalising any thrown error into a typed
 * `DecryptionError` with the supplied `reason`. If the wrapped function
 * already throws a `DecryptionError`, it is rethrown unchanged so nested
 * usages preserve the original reason.
 *
 * Test-only: exported for unit coverage of the wrapper itself.
 */
export const withDecryptionErrorHandling = async <T>(
  reason: DecryptionFailureReason,
  fn: () => Promise<T>,
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (isDecryptionError(error)) {
      throw error;
    }
    logDecryptionFailure(reason, error);
    throw new DecryptionError(reason, error);
  }
};

/**
 * Discriminated result for the consumer-side wrapper. `data` is only present
 * on success.
 */
export type DecryptionAttempt<T> = { ok: true; data: T } | { ok: false };

/**
 * Convenience wrapper used by call sites that want to render gracefully on
 * failure without a try/catch dance. Rethrows non-DecryptionError errors so
 * unexpected bugs (auth failures, programmer errors) are still surfaced.
 *
 * Usage:
 *   const result = await tryDecrypt(() => decryptMessageContent(...));
 *   if (!result.ok) { renderFallback(); return; }
 *   useDecryptedContent(result.data);
 */
export const tryDecrypt = async <T>(
  fn: () => Promise<T>,
): Promise<DecryptionAttempt<T>> => {
  try {
    return { ok: true, data: await fn() };
  } catch (error) {
    if (!isDecryptionError(error)) {
      throw error;
    }
    return { ok: false };
  }
};

// Test-only hook: lets unit tests reset the rate-limiter state between cases.
// Not part of the public API; kept exported because `vi.spyOn` on a private
// closure is impossible.
export const _resetDecryptionLogRateLimiter = (): void => {
  lastLoggedAt.clear();
  inTabFailureCounts.clear();
};
